import List from "src/dataTypes/lists/List";
import StringList from "src/dataTypes/strings/StringList";
import Interval from "src/dataTypes/numeric/Interval";
import NumberOperators from "src/operators/numeric/NumberOperators";
import { typeOf, isArray } from "src/tools/utils/code/ClassUtils";

NumberList.prototype = new List();
NumberList.prototype.constructor = NumberList;

/**
 * @classdesc List structure for Numbers. Provides basic data type for
 * storing and working with numbers in a List.
 *
 * Additional functions that work on NumberList can be found in:
 * <ul>
 *  <li>Operators:   {@link NumberListOperators}</li>
 *  <li>Conversions: {@link NumberListConversions}</li>
 *  <li>Generators: {@link NumberListGenerators}</li>
 * </ul>
 *
 * @constructor
 * @description Creates a new NumberList.
 * @category numbers
 */
function NumberList() {
  var args = [];
  var l = arguments.length;

  for(var i = 0; i < l; i++) {
    args[i] = Number(arguments[i]);
  }
  var array = List.apply(this, args);
  array = NumberList.fromArray(array);
  return array;
}
export default NumberList;

/**
 * Creates a new NumberList from a raw array of numbers.
 *
 * @param {Number[]} array The array of numbers to create the list from.
 * @param {Boolean} forceToNumber If true, explicitly converts values in array to Numbers.
 * @return {NumberList} New NumberList containing values in array
 */
NumberList.fromArray = function(array, forceToNumber) {
  forceToNumber = forceToNumber == null ? true : forceToNumber;

  var result = List.fromArray(array);
  var l = result.length;

  if(forceToNumber) {
    for(var i = 0; i < l; i++) {
      result[i] = Number(result[i]);
    }
  }

  result.type = "NumberList";

  //assign methods to array:
  result.unit = NumberList.prototype.unit;
  result.tenPower = NumberList.prototype.tenPower;
  result.getMin = NumberList.prototype.getMin;
  result.getMax = NumberList.prototype.getMax;
  result.getAmplitude = NumberList.prototype.getAmplitude;
  result.getIndexOfMin = NumberList.prototype.getIndexOfMin;
  result.getIndexOfMax = NumberList.prototype.getIndexOfMax;
  //result.getMinMaxInterval = NumberList.prototype.getMinMaxInterval;
  result.getSum = NumberList.prototype.getSum;
  result.getProduct = NumberList.prototype.getProduct;
  result.getInterval = NumberList.prototype.getInterval;
  result.getIntervalWithAverage = NumberList.prototype.getIntervalWithAverage;
  result.getNumbersSimplified = NumberList.prototype.getNumbersSimplified;

  //statistics
  result.getAverage = NumberList.prototype.getAverage;
  result.getNorm = NumberList.prototype.getNorm;
  result.getStandardDeviation = NumberList.prototype.getStandardDeviation;
  result.getVariance = NumberList.prototype.getVariance;
  result.getMedian = NumberList.prototype.getMedian;
  result.getQuantiles = NumberList.prototype.getQuantiles;

  //sorting
  result.getSorted = NumberList.prototype.getSorted;
  result.getSortIndexes = NumberList.prototype.getSortIndexes;

  //clean
  result.cleanNonFinites = NumberList.prototype.cleanNonFinites;

  //
  result.getNormalized = NumberList.prototype.getNormalized;
  result.getNormalizedToMax = NumberList.prototype.getNormalizedToMax;

  //math
  result.factor = NumberList.prototype.factor;
  result.add = NumberList.prototype.add;
  result.subtract = NumberList.prototype.subtract;
  result.divide = NumberList.prototype.divide;
  result.sqrt = NumberList.prototype.sqrt;
  result.pow = NumberList.prototype.pow;
  result.log = NumberList.prototype.log;
  result.floor = NumberList.prototype.floor;
  result.isEquivalent = NumberList.prototype.isEquivalent;
  result.addRandom = NumberList.prototype.addRandom;
  result.trim = NumberList.prototype.trim;

  result.getGeometricMean = NumberList.prototype.getGeometricMean;
  

  //transform
  result.approach = NumberList.prototype.approach;

  //override
  result.clone = NumberList.prototype.clone;
  //result._slice = Array.prototype.slice;
  //result.slice = NumberList.prototype.slice;

  return result;
};

NumberList.prototype.unit = "";
NumberList.prototype.tenPower = 0;

/**
 * Returns minimum value in the List. Null if the NumberList is empty.
 *
 * @return {Number} The min value.
 */
NumberList.prototype.getMin = function() {
  //TODO:store result and retrieve while the NumberList doesn't change;
  if(this.length === 0) return null;
  var i;
  var min = this[0];
  for(i = 1; i < this.length; i++) {
    min = Math.min(min, this[i]);
  }
  return min;
};

/**
 * Returns maximum value in the List. Null if the NumberList is empty.
 *
 * @return {Number} The max value.
 */
NumberList.prototype.getMax = function() {
  //TODO:store result and retrieve while the NumberList doesn't change;
  if(this.length === 0) return null;
  var i;
  var max = this[0];
  var l = this.length;
  for(i = 1; i < l; i++) {
    max = Math.max(max, this[i]);
  }
  return max;
};

/**
 * Returns index of minimum value in the List. Null if the NumberList is empty.
 * @return {Number} The index of min.
 * tags:
 */
NumberList.prototype.getIndexOfMin = function() {
  if(this.length === 0) return null;
  var i;
  var min = this[0];
  var imin = 0;
  for(i = 1; i < this.length; i++) {
    if(this[i] < min){
      min = this[i];
      imin = i;
    }
  }
  return imin;
};

/**
 * Returns index of maximum value in the List. Null if the NumberList is empty.
 * @return {Number} The index of max.
 * tags:
 */
NumberList.prototype.getIndexOfMax = function() {
  if(this.length === 0) return null;
  var i;
  var max = this[0];
  var imax = 0;
  for(i = 1; i < this.length; i++) {
    if(this[i] > max){
      max = this[i];
      imax = i;
    }
  }
  return imax;
};

/**
 * Finds the range of the values in the NumberList.
 *
 * @return {Number} The difference between the minimum and maximum value in the List.
 */
NumberList.prototype.getAmplitude = function() {
  if(this.length === 0) return 0;
  var min = this[0];
  var max = this[0];
  var l = this.length;
  for(var i = 1; i<l; i++) {
    min = Math.min(min, this[i]);
    max = Math.max(max, this[i]);
  }
  return max - min;
};

/**
 * Provides the min and max values as an {@link Interval}.
 *
 * @return {Interval} Interval containing the min and max values of the List.
 */
// NumberList.prototype.getMinMaxInterval = function() { //deprecated?
//   return new Interval(this.getMin(), this.getMax());
// };

/**
 * Returns the total sum of values in the NumberList.
 *
 * @return {Number} Sum of all values in the List.
 * tags:math
 */
NumberList.prototype.getSum = function() {
  if(this.length === 0) return 0;
  var i;
  var sum = this[0];
  var l = this.length;
  for(i = 1; i < l; i++) {
    sum += this[i];
  }
  return sum;
};

/**
 * Returns the product of values in the NumberList.
 *
 * @return {Number} The product of all values in the NumberList.
 * tags:math
 */
NumberList.prototype.getProduct = function() {
  if(this.length === 0) return null;
  var i;
  var product = this[0];
  var l = this.length;
  for(i = 1; i < l; i++) {
    product *= this[i];
  }
  return product;
};

/**
 * Builds an Interval with min and max value from the NumberList
 * @return {Interval} with starting value as the min of the NumberList
 * and ending value as the max.
 */
NumberList.prototype.getInterval = function(bIgnoreNaN) {
  if(this.length === 0) return null;
  bIgnoreNaN = bIgnoreNaN == null ? false : bIgnoreNaN;
  var max = -Infinity;
  var min = Infinity;
  var l = this.length;
  var i;
  for(i = 0; i<l; i++) {
    if(bIgnoreNaN && isNaN(this[i])) continue;
    max = Math.max(max, this[i]);
    min = Math.min(min, this[i]);
  }
  if(max == -Infinity && min == Infinity){
    // only happens when no valid numeric entries. Switch to natural order
    min = -Infinity;
    max =  Infinity;
  }
  var interval = new Interval(min, max);
  return interval;
};

/**
 * Builds an Interval with min and max value from the NumberList, with an average property added
 * @return {Interval} with starting value as the min of the NumberList
 * and ending value as the max.
 */
NumberList.prototype.getIntervalWithAverage = function() {
  if(this.length === 0) return null;
  var max = this[0];
  var min = this[0];
  var average = this[0];
  var l = this.length;
  var i;
  for(i = 1; i<l; i++) {
    max = Math.max(max, this[i]);
    min = Math.min(min, this[i]);
    average+=this[i];
  }
  var interval = new Interval(min, max);
  interval.average = average/l;
  return interval;
};




/**
 * simplifies the numberList either by replacing numbers by its order of magnitude, or by quantiles
 *
 * @param  {Number} method simplification method:<|>0:factors of numbers of magnitude<|>1:number of quantile<|>2:rounded by quantile<|>3:order of magnitude<|>4:rounded by order of magnitude<|>5:sigificant digits (num.toPrecision)<|>quantile interval
 * @param  {Number} param different meaning according to choosen method:<br>0:number of significant digits<br>1:number of quantiles<br>2:number of quantiles<br>3:no need of param<br>4:no need of param<br>5:number of significant digits<br>6:number of quantiles
 * @return {NumberList} simplified list
 * tags:transform
 */
NumberList.prototype.getNumbersSimplified = function(method, param) {
  var newList;
  var i, j;
  var l = this.length;

  method = method||0;
  param = param||0;

  newList = new NumberList();
  newList.name = this.name;

  switch(method){
    case 0:
      var power = Math.pow(10, param);
      this.forEach(function(val){
        newList.push(Math.floor(val/power)*power);
      });
      break;
    case 6:
      newList = this.getQuantiles(param, 2);
      newList.name = this.name;// + " ("+param+" quantiles intervals)";
      break;
    case 1:
    case 2:
      param = Math.min( param||10, Math.floor(this.length/2) );
      var quantiles = this.getQuantiles(param);
      var val;
      var nQuantiles = quantiles.length;
      for(i=0; i<l; i++){
        val = this[i];
        if(val<quantiles[0]){
          method==1?newList.push(0):newList.push(quantiles._min);
        } else {
          for(j=0; j<nQuantiles; j++){
            if( val>=quantiles[j] && (j+1==quantiles.length || val<quantiles[j+1]) ){
              method==1?newList.push(j+1):newList.push(quantiles[j]);
              break;
            }
          }
        }
      }
      if(method==1) newList.name = this.name + " (n quantile)";
      break;
    case 3:
      newList.name = this.name + " (order of magnitude)";
      for(i=0; i<l; i++){
        newList.push(Math.floor( Math.log(this[i])/Math.log(10) ));
      }
      // this.forEach(function(val){
      //   newList.push(Math.floor( Math.log(val)/Math.log(10) ));
      // });
      break;
    case 4:
      newList.name = this.name + " (rounded by order of magnitude)";
      for(i=0; i<l; i++){
        newList.push( Math.pow ( 10, Math.floor( Math.log(this[i])/Math.log(10) ) ) );
      }
      // this.forEach(function(val){
      //   newList.push( Math.pow ( 10, Math.floor( Math.log(val)/Math.log(10) ) ) );
      // });
      break;
    case 5:
      param = param||1;
      newList.name = this.name + " (significant digits)";
      for(i=0; i<l; i++){
        newList.push( Number(this[i].toPrecision(param)) );
      }
      break;
  }

  return newList;
};


/**
 * Builds an {@link Polygon} from the NumberList,
 * using each consecutive pair of values in the numberList as
 * x and y positions.
 *
 * @return {Polygon} Polygon representing the values
 * in the NumberList as x/y coordinates.
 */
NumberList.prototype.toPolygon = function() {
  if(this.length === 0) return null;
  var polygon = new Polygon();
  for(var i = 0; this[i + 1] != null; i += 2) {
    polygon.push(new Point(this[i], this[i + 1]));
  }
  return polygon;
};


/////////statistics

/**
 * Calculates mean of the NumberList.
 *
 * @return {Number} Mean of all values in the List.
 * tags:statistics,extract
 */
NumberList.prototype.getAverage = function() {//@todo: change name to getMean
  return this.getSum()/this.length;
};

/**
 * Calculates the geometric mean of the NumberList.
 *
 * @return {Number}
 * tags:statistics,extract
 */
NumberList.prototype.getGeometricMean = function() {
  /*
  var s = 0;
  //this.forEach(function(val) {
  var l = this.length;
  for(var i = 0; i<l; i++) {
    s += Math.log(this[i]);
  }
  return Math.pow(Math.E, s / this.length);
  */
 if(this.length==0) return 0;
 var s = this[0];
  for(var i = 1; i<this.length; i++) {
    s *= this[i];
  }
  return Math.pow(s, 1 / this.length);
};

/**
 * Calculates the norm of the NumberList (treated as a vector).
 *
 * @return {Number}
 * tags:statistics,extract
 */
NumberList.prototype.getNorm = function() {
  var sq = 0;
  var l = this.length;
  for(var i = 0; i<l; i++) {
    sq += Math.pow(this[i], 2);
  }
  return Math.sqrt(sq);
};

/**
 * Calculates the variance of the NumberList.
 *
 * @return {Number}
 * tags:statistics,extract
 */
NumberList.prototype.getVariance = function() {
  var sd = 0;
  var average = this.getAverage();
  var l = this.length;
  for(var i = 0; i<l; i++) {
    sd += Math.pow(this[i] - average, 2);
  }
  return sd / this.length;
};

/**
 * Calculates the standard deviation.
 *
 * @return {Number}
 * tags:statistics,extract
 */
NumberList.prototype.getStandardDeviation = function() {
  return Math.sqrt(this.getVariance());
};

/**
 * Calculates the median of the numberList
 *
 * @return {Number}
 * tags:statistics,extract
 */
NumberList.prototype.getMedian = function() {
  var sorted = this.getSorted(true);
  var prop = (this.length - 1) / 2;
  var entProp = Math.floor(prop);
  var onIndex = prop == entProp;
  return onIndex ? sorted[prop] : (0.5 * sorted[entProp] + 0.5 * sorted[entProp + 1]);
};

/**
 * Builds a partition of n quantiles from the numberList.
 *
 * @param {Number} nQuantiles number of quantiles (the size of the resulting list is nQuantiles-1) (For getting segments with predefined limits, use segmentNumberList)
 * @param {Number} Number return mode<|>0:quantile values<|>1:list of number of quantile<|>2:list of intervals<|>3:words "low", ("medium",) "high" (in case of 2 or 3 quantiles) or Qi for more than 3
 * @return {NumberList} A number list of the quantiles.
 * tags:statistics,extract
 */
NumberList.prototype.getQuantiles = function(nQuantiles, mode) {//TODO: defines different options for return
  nQuantiles = nQuantiles==null?4:nQuantiles;
  mode = mode || 0;

  var l = this.length;
  var sorted = this.getSorted(true);
  var prop = l/nQuantiles;
  var entProp = Math.floor(prop);
  var onIndex = prop == entProp;
  var quantiles = new NumberList();
  quantiles.name = this.name;
  var i;

  for(i = 0; i < nQuantiles - 1; i++) {
    quantiles[i] = onIndex ? sorted[(i + 1) * prop] : (0.5 * sorted[(i + 1) * entProp] + 0.5 * sorted[(i + 1) * entProp + 1]);
  }

  quantiles._sorted = sorted;
  quantiles._min = sorted[0];
  quantiles._max = sorted[sorted.length-1];

  if(mode===0) return quantiles;

  var numberQuantil;
  if(mode==1){
    numberQuantil= new NumberList();
  } else {
     numberQuantil= new StringList();
  }
  
  numberQuantil.name = this.name;
  var j;
  var words;
  if(mode==3){
    if(nQuantiles <= 3)
      words = nQuantiles==2?['low', 'high']:['low', 'medium', 'high'];
    else{
      words=[]
      for(i=0;i<nQuantiles;i++)
        words.push('Q'+(i+1))
    }
  }

  for(i=0; i<l; i++){
    for(j=0; j<quantiles.length; j++){
      if(this[i]<quantiles[j]){
        numberQuantil[i] = mode==3?words[j]:(mode==1?j:( (j==0?quantiles._min:quantiles[j-1]) + "-" + quantiles[j] ));
        break;
      }
    }
    if(numberQuantil[i]==null) numberQuantil[i] = mode==3?words[j]:(mode==1?quantiles.length:(quantiles[quantiles.length-1]+"-"+quantiles._max));
  }

  numberQuantil._quantiles = quantiles;
  return numberQuantil;

};

/////////sorting

/**
 * Returns a new NumberList sorted in either ascending or descending order.
 *
 * @param {Boolean} ascending True if values should be sorted in ascending order.
 * If false, values will be sorted in descending order.
 * @return {NumberList} new sorted NumberList.
 */
NumberList.prototype.getSorted = function(ascending) {
  ascending = ascending == null ? true : ascending;

  var newList;
  if(ascending) {
    newList = NumberList.fromArray(this.slice().sort(function(a, b) {
      return a - b;
    }), false);
  }
  else {
    newList = NumberList.fromArray(this.slice().sort(function(a, b) {
      return b - a;
    }), false);
  }
  newList.name = this.name;
  return newList;
};

/**
 * Returns a new NumberList containing the indicies of the values of
 * the original NumberList in sorted order.
 *
 * @param {Boolean} descending If true, values are sorted in descending order.
 * @return {NumberList} NumberList containing the indices of the original NumberList
 * such that accessing the values of the original list at those indices would produce
 * a sorted list.
 * @example
 * var nl = NumberList.fromArray([1,3,2]);
 * var indices = nl.getSortIndexes();
 * indices[0]; // produces 1 as nl[1] == 3.
 */
NumberList.prototype.getSortIndexes = function(descending) {
  if(descending == null) descending = true;

  var pairs = [];
  var newList = new NumberList();
  var l = this.length;

  if(this.length === 0) return newList;

  for(var i = 0; i<l; i++) {
    pairs.push([i, this[i]]);
  }

  if(descending) {
    pairs.sort(function(a, b) {
      if(a[1] < b[1]) return 1;
      return -1;
    });
  } else {
    pairs.sort(function(a, b) {
      if(a[1] < b[1]) return -1;
      return 1;
    });
  }

  for(i = 0; pairs[i] != null; i++) {
    newList.push(pairs[i][0]);
  }
  newList.name = this.name;
  return newList;
};


/**
 * replace NaNs, Infinities and negative Ifinities by 0, min-1, max+1, respectively
 * @return {NumberList}
 * tags:transform,clean
 * examples:santiago/examples/operators/cleanNonInfinities
 */
NumberList.prototype.cleanNonFinites = function(interval){
  if(interval==null) interval = this.getInterval();

  if( !isFinite(interval.x) || !isFinite(interval.y) ){

    var min;
    var max;

    var negInfinities = new mo.NumberList();
    var posInfinities = new mo.NumberList();
    var nans = new mo.NumberList();

    var newList = new mo.NumberList();
    newList.name = this.name;

    for(i = 0; i < this.length; i++) {
        if( !isFinite(this[i]) ){
          if(this[i]==Infinity){
            posInfinities.push(i);
          } else if(this[i]==-Infinity){
            negInfinities.push(i);
          } else {
            nans.push(i);
          }
        } else {
          if(min==null) min = this[i];
          if(max==null) max = this[i];
          min = Math.min(min, this[i]);
          max = Math.max(max, this[i]);
          newList[i] = this[i];
        }
     }
     if(min==null) min = 0;
     if(max==null) max = 0;
     min--;
     max++;

     for(i = 0; i < negInfinities.length; i++) {
      newList[negInfinities[i]] = min;
     }
     for(i = 0; i < posInfinities.length; i++) {
      newList[posInfinities[i]] = max;
     }
     for(i = 0; i < nans.length; i++) {
      newList[nans[i]] = 0;
     }

     return newList;
  }
  return this;
}


/**
 * returns a NumberList normalized to min-max interval
 *
 * @param {Number} factor Optional multiplier to modify the normalized values by. Defaults to 1.
 * @return {NumberList} (adds an interval property with original list min max interval)
 * tags:transform
 */
NumberList.prototype.getNormalized = function(factor) {
  if(this==null) return;

  factor = factor == null ? 1 : factor;

  if(this.length === 0) return null;
  
  var i;
  var interval = this.getInterval();

  var newList = this.cleanNonFinites(interval);
  if(this!=newList) return newList.getNormalized(factor);

  var a = interval.getAmplitude();
  var newNumberList;
  if(a===0) {
    newNumberList = mo.ListGenerators.createListWithSameElement(this.length, 1);
    newNumberList.name = this.name;
    newNumberList.interval = interval;
    return newNumberList;
  }
  newNumberList = new NumberList();
  factor /= a;
  for(i = 0; i < this.length; i++) {
    newNumberList[i] = factor*(this[i] - interval.x);
  }
  newNumberList.name = this.name;
  newNumberList.interval = interval;
  return newNumberList;
};

/**
 * returns a NumberList normalized to max value
 *
 * @param {Number} factor Optional multiplier to modify the normalized value
 * @return {NumberList}
 * tags:transform
 */
NumberList.prototype.getNormalizedToMax = function(factor) {
  if(this==null) return;

  factor = factor == null ? 1 : factor;

  if(this.length === 0) return null;
  
  var i;
  var max = this.getMax();

  var newList = this.cleanNonFinites();
  if(this!=newList) return newList.getNormalizedToMax(factor);


  var newNumberList = new NumberList();
  for(i = 0; i < this.length; i++) {
    newNumberList[i] = factor * this[i]/max;
  }
  newNumberList.name = this.name;
  return newNumberList;
};



/**
 * Returns a new NumberList with the values of
 * the original list multiplied by the input value
 *
 * @param {Number|NumberList} value number or NumberList, the value(s) to multiply each value in the list by.
 * @return {NumberList} New NumberList with values multiplied.
 * tags:math
 */
NumberList.prototype.factor = function(value) {
  var i;
  var newNumberList = new NumberList();
  var l = this.length;

  if(Array.isArray(value)){
    for(i = 0; i < l; i++) {
      newNumberList.push(this[i] * value[i%this.length]);
    }
  } else {
    for(i = 0; i < l; i++) {
      newNumberList.push(this[i] * value);
    }
  }
  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * Adds a value or values in a NumberList to the current list.
 *
 * If input is a Number, each value of the returned
 * NumberList will be the sum of the original value and this
 * input value.
 *
 * If the input is a NumberList, each value of the returned
 * NumberList will be the sum of the original value and the
 * value at the same index in the input list.
 *
 * @param {Number|NumberList} object Input value to add to the list.
 * @return {NumberList}
 * tags:math
 */
NumberList.prototype.add = function(object) {
  var i;
  var newNumberList = new NumberList();
  var type = typeOf(object);
  var l = this.length;

  switch(type) {
    case 'number':
      for(i = 0; i<l; i++) {
        newNumberList[i] = this[i] + object;
      }
      break;
    case 'NumberList':
      for(i = 0; i<l; i++) {
        newNumberList[i] = this[i] + object[i % object.length];
      }
      break;
  }

  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * Subtracts a value or values in a NumberList from the current list.
 *
 * If input is a Number, each value of the returned
 * NumberList will be the original value minus this
 * input value.
 *
 * If the input is a NumberList, each value of the returned
 * NumberList will be the original value minus the
 * value at the same index in the input list.
 *
 * @param {Number|NumberList} object Input value to subract from the list.
 * @return {NumberList}
 * tags:math
 */
NumberList.prototype.subtract = function(object) {
  var i;
  var newNumberList = new NumberList();
  var type = typeOf(object);
  var l = this.length;

  switch(type) {
    case 'number':
      for(i = 0; i<l; i++) {
        newNumberList[i] = this[i] - object;
      }
      break;
    case 'NumberList':
      for(i = 0; i<l; i++) {
        newNumberList[i] = this[i] - object[i % object.length];
      }
      break;
  }

  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * Returns a new NumberList with each value divided by a input value or values in a NumberList.
 *
 * If input is a Number, each value of the returned
 * NumberList will be the original value divided by this
 * input value.
 * If the input is a NumberList, each value of the returned
 * NumberList will be the original value divided by the
 * value at the same index in the input list.
 * @param {Number|NumberList} object Input value to divide by the list.
 * @return {NumberList}
 * tags:math
 */
NumberList.prototype.divide = function(object) {
  var i;
  var newNumberList = new NumberList();
  var type = typeOf(object);
  var l = this.length;

  switch(type) {
    case 'number':
      for(i = 0; i<l; i++) {
        newNumberList[i] = this[i] / object;
      }
      break;
    case 'NumberList':
      for(i = 0; i<l; i++) {
        newNumberList[i] = this[i] / object[i % object.length];
      }
      break;
  }

  newNumberList.name = this.name;
  return newNumberList;
};


/**
 * Returns a new NumberList containing the square root of
 * the values of the current NumberList.
 *
 * @return {NumberList} NumberList with square rooted values.
 */
NumberList.prototype.sqrt = function() {
  var i;
  var newNumberList = new NumberList();
  var l = this.length;

  for(i = 0; i < l; i++) {
    newNumberList.push(Math.sqrt(this[i]));
  }
  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * Returns a new NumberList containing values raised to the power
 * of the input value.
 *
 * @param {Number} power Power to raise each value by.
 * @return {NumberList} New NumberList.
 */
NumberList.prototype.pow = function(power) {
  var i;
  var newNumberList = new NumberList();
  var l = this.length;
  for(i = 0; i < l; i++) {
    newNumberList.push(Math.pow(this[i], power));
  }
  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * Returns a transformed version of the list with
 * each value in the new list the log of the value
 * in the current list, with an optional constant
 * added to it.
 *
 * @param {Number} add Optional value to add to the log transformed values.
 * Defaults to 0.
 * @return {NumberList}
 */
NumberList.prototype.log = function(add) {
  add = add || 0;

  var i;
  var newNumberList = new NumberList();
  var l = this.length;
  for(i = 0; i<l; i++) {
    newNumberList[i] = Math.log(this[i] + add);
  }
  newNumberList.name = this.name;

  return newNumberList;
};

/**
 * Returns a new NumberList containing the floor values (removing decimals) of the values of the current NumberList.
 * @return {NumberList} NumberList with integer values.
 */
NumberList.prototype.floor = function() {
  var i;
  var newNumberList = new NumberList();
  var l = this.length;
  for(i = 0; i < l; i++) {
    newNumberList.push(Math.floor(this[i]));
  }
  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * @todo write docs
 */
NumberList.prototype.approach = function(destinty, speed) {
  speed = speed || 0.5;

  var i;
  var antispeed = 1 - speed;

  for(i = 0; this[i] != null; i++) {
    this[i] = antispeed * this[i] + speed * destinty[i];
  }
};

/**
 * Returns true if values in the input NumberList are the same as the values in the current list.
 * @param  {NumberList} numberList2 Second NumberList to compare.
 * @return {Boolean} True if all values in both lists match.
 */
NumberList.prototype.isEquivalent = function(numberList) {
  var l = this.length;
  if(numberList == null || numberList.length != l) {
    return false;
  }
  for(var i = 0; i<l; i++) {
    if(this[i] != numberList[i]) return false;
  }
  return true;
};

/**
 * adds random numbers to numbers in a numberList
 *
 * @param {Interval} optional interval for random numbers ([0,1] by default)
 * @return {NumberList}
 * tags:transform
 */
NumberList.prototype.addRandom = function(interval){
  if(interval == null) interval = new Interval(0,1);
  var i;
  var newNumberList = new NumberList();
  var l = this.length;
  var a = interval.getAmplitude();

  for(i = 0; i < l; i++) {
    newNumberList[i] =  this[i] + NumberOperators.random()*a + interval.x;
  }
  
  newNumberList.name = this.name;
  return newNumberList;
};

/**
 * trim a numberList by a min and max values
 *
 * @param {Number} min value, any value below will be replaced by min
 * @param {Number} max value, any value above will be replaced by max
 * @return {NumberList}
 */
NumberList.prototype.trim = function(min, max){
  if(min==null && max==null) return this;
  var newNumberList = new NumberList();

  for(var i = 0; i < this.length; i++) {
    if(min!=null) newNumberList[i] =  Math.max(this[i], min);
    if(max!=null) newNumberList[i] =  Math.min(this[i], max);
  }
  
  newNumberList.name = this.name;
  return newNumberList;
};

//transform

NumberList.prototype.approach = function(destinty, speed) {
  speed = speed || 0.5;

  var i;
  var antispeed = 1 - speed;
  var l = this.length;

  for(i = 0; i<l; i++) {
    this[i] = antispeed * this[i] + speed * destinty[i];
  }
  return true;
};

///////overriding

/**
 * @todo write docs
 */
NumberList.prototype.clone = function() {
  var newList = NumberList.fromArray(this.slice(), false);
  newList.name = this.name;
  if(this.fnDerived)
    newList.fnDerived = this.fnDerived;
  return newList;
};

/**
 * @todo write docs
 */
// NumberList.prototype.slice = function() {
//   return NumberList.fromArray(this._slice.apply(this, arguments), false);
// };
